import { useReducer, useEffect } from 'react';
import { useQueryWithFarmId } from '../utils/hooks';
import { FarmQuery } from '../queries';

export const baseLayerTypes = {
  NAIP_SATELLITE: 'naip',
  MAPBOX_SATELLITE: 'mapbox',
  AZURE_SATELLITE: 'azure',
  NO_SATELLITE: 'noSatellite',
};

const defaultState = {
  baseLayer: baseLayerTypes.NAIP_SATELLITE,
  contour: false,
  roads: true,
  dark: false,
};

const baseLayerReducer = (state, action) => {
  switch (action.type) {
    case 'reset': {
      // If we have farm settings, use them instead of defaults
      if (action.farm) {
        return {
          baseLayer: action.farm.defaultBaseMap || defaultState.baseLayer,
          contour:
            action.farm.baseLayerContoursEnabled !== undefined
              ? action.farm.baseLayerContoursEnabled
              : defaultState.contour,
          roads:
            action.farm.baseLayerRoadsEnabled !== undefined
              ? action.farm.baseLayerRoadsEnabled
              : defaultState.roads,
          dark:
            action.farm.baseLayerDarkEnabled !== undefined
              ? action.farm.baseLayerDarkEnabled
              : defaultState.dark,
        };
      }
      return defaultState;
    }
    case 'toggleContour':
      return { ...state, contour: !state.contour };
    case 'toggleRoads':
      return { ...state, roads: !state.roads };
    case 'toggleDark':
      return { ...state, dark: !state.dark };
    default: {
      return { ...state, ...action.payload };
    }
  }
};

const useBaseLayer = () => {
  const { data } = useQueryWithFarmId(FarmQuery);
  const [baseLayerState, dispatchBaseLayer] = useReducer(
    baseLayerReducer,
    defaultState,
  );

  // Initialize from farm settings when farm ID changes
  useEffect(() => {
    if (data?.farm.id) {
      dispatchBaseLayer({ type: 'reset', farm: data.farm });
    }
  }, [data?.farm.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setBaseLayer = (baseLayerType) =>
    dispatchBaseLayer({ payload: { baseLayer: baseLayerType } });

  const toggleContour = () => dispatchBaseLayer({ type: 'toggleContour' });

  const toggleRoads = () => dispatchBaseLayer({ type: 'toggleRoads' });

  const toggleDark = () => dispatchBaseLayer({ type: 'toggleDark' });

  const resetBaseLayer = () =>
    dispatchBaseLayer({ type: 'reset', farm: data.farm });

  const setBaseLayerState = (payload) => dispatchBaseLayer({ payload });

  const { baseLayer, contour, roads, dark } = baseLayerState;

  return {
    baseLayer,
    contour,
    roads,
    dark,
    setBaseLayer,
    toggleContour,
    toggleRoads,
    toggleDark,
    setBaseLayerState,
    resetBaseLayer,
  };
};

export default useBaseLayer;
